import { percentileRange } from './palettes';

/*
 * ROI statistics on a thermal matrix, in the browser: the same rule, the same
 * order and the same arithmetic as mushroom-bank/lambda/thermal/roi.py, so
 * the panel's live readouts are the numbers the server will store.
 * reference_data/thermal_roi_cases.json holds both to them (roi.test.js
 * here, test_thermal_roi.py there).
 *
 * Geometry is in matrix pixels: x grows to the right from the left edge (0)
 * to the right edge (width), y downwards from the top. Pixel (column c,
 * row r) covers [c, c+1) x [r, r+1); its centre is (c + 0.5, r + 0.5).
 *
 * A PIXEL BELONGS TO A SHAPE WHEN ITS CENTRE DOES:
 *
 *   spot     {x, y}           the one pixel containing the point; its 3 x 3
 *                             mean (clipped to the frame, NaN skipped) is
 *                             spot_mean3_c
 *   rect     {x, y, w, h}     x <= cx < x + w and y <= cy < y + h (half-open)
 *   ellipse  {cx, cy, rx, ry} ((px - cx)/rx)^2 + ((py - cy)/ry)^2 <= 1
 *   polygon  {points}         3 to 200 [x, y] vertices, even-odd rule, the
 *                             crossings of each row's centre line half-open
 *
 * Pixels outside the frame are dropped. A NaN pixel is a member but is not
 * counted; with no valid pixel every statistic is null. Statistics run row by
 * row, left to right: min_at and max_at are the first pixel holding the
 * minimum and the maximum, and the mean is the running sum over the count.
 *
 * A HISTOGRAM (plan phase 10; roi.py histogram) counts an ROI's valid pixels
 * in BINS = 32 bins over [lo, hi], the analysis's palette range, shared by
 * every ROI of the analysis: a NaN pixel goes nowhere, t < lo to `under`,
 * t > hi to `over`, otherwise to bin min(31, floor(32 * (t - lo) / (hi - lo)))
 * -- that expression, in that order -- or, when hi == lo, to bin 0.
 *
 * The server recomputes every value it stores (section 38, principle 4):
 * what this file gives the panel is provisional.
 */
export const SHAPES = Object.freeze(['spot', 'rect', 'ellipse', 'polygon']);
export const RANGE_MODES = Object.freeze(['frame', 'percentile', 'rois', 'manual']);
export const MAX_ROIS = 20;
export const MAX_POINTS = 200;
export const MAX_LABEL = 40;
/** A histogram's bins (roi.py BINS). */
export const BINS = 32;
/** Okabe-Ito: told apart by every common colour-vision deficiency (roi.py COLOURS). */
export const COLOURS = Object.freeze(['#E69F00', '#56B4E9', '#009E73', '#F0E442', '#0072B2', '#D55E00',
  '#CC79A7', '#000000']);
export const KEYS = Object.freeze({
  spot: ['x', 'y'], rect: ['x', 'y', 'w', 'h'], ellipse: ['cx', 'cy', 'rx', 'ry'], polygon: ['points'],
});
/** Coordinates beyond this are a mistake, not a shape. */
export const LIMIT = 100000.0;

function number(value, name) {
  if (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value) > LIMIT) {
    throw new Error(`${name} is a finite number`);
  }
  return value;
}

const sameKeys = (geometry, keys) => {
  const own = Object.keys(geometry);
  return own.length === keys.length && keys.every(k => own.includes(k));
};

/** The canonical geometry, or an Error saying what is wrong (roi.py normalise). */
export function normalise(shape, geometry) {
  if (!SHAPES.includes(shape)) throw new Error(`shape is one of ${SHAPES.join(', ')}`);
  if (!geometry || typeof geometry !== 'object' || Array.isArray(geometry) || !sameKeys(geometry, KEYS[shape])) {
    throw new Error(`a ${shape} is {${KEYS[shape].join(', ')}}`);
  }
  if (shape === 'polygon') {
    const { points } = geometry;
    if (!Array.isArray(points) || points.length < 3 || points.length > MAX_POINTS) {
      throw new Error(`a polygon has 3 to ${MAX_POINTS} points`);
    }
    return {
      points: points.map((p) => {
        if (!Array.isArray(p) || p.length !== 2) throw new Error("a polygon's point is [x, y]");
        return [number(p[0], 'x'), number(p[1], 'y')];
      }),
    };
  }
  const out = {};
  for (const k of KEYS[shape]) out[k] = number(geometry[k], k);
  for (const k of ['w', 'h', 'rx', 'ry']) {
    if (k in out && out[k] <= 0) throw new Error(`${k} is above 0`);
  }
  return out;
}

const clip = (lo, hi, size) => [Math.max(0, lo), Math.min(size - 1, hi)];

/** [column, row] of every pixel in the shape, row by row, left to right (roi.py pixels). */
export function pixels(width, height, shape, g) {
  const out = [];
  if (shape === 'spot') {
    const c = Math.floor(g.x);
    const r = Math.floor(g.y);
    if (c >= 0 && c < width && r >= 0 && r < height) out.push([c, r]);
    return out;
  }
  if (shape === 'rect') {
    const x0 = g.x;
    const x1 = g.x + g.w;
    const y0 = g.y;
    const y1 = g.y + g.h;
    const [cLo, cHi] = clip(Math.floor(x0) - 1, Math.ceil(x1) + 1, width);
    const [rLo, rHi] = clip(Math.floor(y0) - 1, Math.ceil(y1) + 1, height);
    for (let r = rLo; r <= rHi; r += 1) {
      const cy = r + 0.5;
      if (!(y0 <= cy && cy < y1)) continue;
      for (let c = cLo; c <= cHi; c += 1) {
        const cx = c + 0.5;
        if (x0 <= cx && cx < x1) out.push([c, r]);
      }
    }
    return out;
  }
  if (shape === 'ellipse') {
    const { cx: cx0, cy: cy0, rx, ry } = g;
    const [cLo, cHi] = clip(Math.floor(cx0 - rx) - 1, Math.ceil(cx0 + rx) + 1, width);
    const [rLo, rHi] = clip(Math.floor(cy0 - ry) - 1, Math.ceil(cy0 + ry) + 1, height);
    for (let r = rLo; r <= rHi; r += 1) {
      const dy = (r + 0.5 - cy0) / ry;
      for (let c = cLo; c <= cHi; c += 1) {
        const dx = (c + 0.5 - cx0) / rx;
        if (dx * dx + dy * dy <= 1.0) out.push([c, r]);
      }
    }
    return out;
  }
  const { points } = g;
  const ys = points.map(p => p[1]);
  const [rLo, rHi] = clip(Math.floor(Math.min(...ys)) - 1, Math.ceil(Math.max(...ys)) + 1, height);
  for (let r = rLo; r <= rHi; r += 1) {
    const cy = r + 0.5;
    const crossings = [];
    for (let i = 0; i < points.length; i += 1) {
      const [xi, yi] = points[i];
      const [xj, yj] = points[(i + 1) % points.length];
      if ((yi > cy) !== (yj > cy)) crossings.push(xi + (cy - yi) * (xj - xi) / (yj - yi));
    }
    crossings.sort((a, b) => a - b);
    for (let k = 0; k < crossings.length - 1; k += 2) {
      const a = crossings[k];
      const b = crossings[k + 1];
      const [cLo, cHi] = clip(Math.floor(a) - 1, Math.ceil(b) + 1, width);
      for (let c = cLo; c <= cHi; c += 1) {
        const cx = c + 0.5;
        if (a <= cx && cx < b) out.push([c, r]);
      }
    }
  }
  return out;
}

function mean3(width, height, temps, g) {
  const c0 = Math.floor(g.x);
  const r0 = Math.floor(g.y);
  let n = 0;
  let total = 0.0;
  for (let r = Math.max(0, r0 - 1); r < Math.min(height, r0 + 2); r += 1) {
    for (let c = Math.max(0, c0 - 1); c < Math.min(width, c0 + 2); c += 1) {
      const t = temps[r * width + c];
      if (!Number.isNaN(t)) {
        n += 1;
        total += t;
      }
    }
  }
  return n ? total / n : null;
}

/** {t_min_c, t_max_c, t_avg_c, pixel_count, min_at, max_at[, spot_mean3_c]} (roi.py stats). */
export function stats(width, height, temps, shape, geometry) {
  const g = normalise(shape, geometry);
  let count = 0;
  let total = 0.0;
  let tMin = null;
  let tMax = null;
  let minAt = null;
  let maxAt = null;
  for (const [c, r] of pixels(width, height, shape, g)) {
    const t = temps[r * width + c];
    if (Number.isNaN(t)) continue;
    count += 1;
    total += t;
    if (tMin === null || t < tMin) { tMin = t; minAt = { x: c, y: r }; }
    if (tMax === null || t > tMax) { tMax = t; maxAt = { x: c, y: r }; }
  }
  const out = {
    t_min_c: tMin, t_max_c: tMax, t_avg_c: count ? total / count : null,
    pixel_count: count, min_at: minAt, max_at: maxAt,
  };
  if (shape === 'spot') out.spot_mean3_c = count ? mean3(width, height, temps, g) : null;
  return out;
}

/** {lo_c, hi_c, counts: [BINS counts], under, over} (roi.py histogram). */
export function histogram(width, height, temps, shape, geometry, lo, hi) {
  const g = normalise(shape, geometry);
  const a = number(lo, 'histogram lo');
  const b = number(hi, 'histogram hi');
  if (a > b) throw new Error("a histogram's lo is at most its hi");
  const counts = new Array(BINS).fill(0);
  let under = 0;
  let over = 0;
  const span = b - a;
  for (const [c, r] of pixels(width, height, shape, g)) {
    const t = temps[r * width + c];
    if (Number.isNaN(t)) continue;
    if (t < a) under += 1;
    else if (t > b) over += 1;
    else if (span > 0) counts[Math.min(BINS - 1, Math.floor(BINS * (t - a) / span))] += 1;
    else counts[0] += 1;
  }
  return { lo_c: a, hi_c: b, counts, under, over };
}

/**
 * [lo, hi], the palette range of a mode, as the server works it out
 * (roi.py range_for): the frame's minimum to maximum, its 2nd to 98th
 * percentile, the ROIs' range, or the two numbers a person typed.
 */
export function rangeFor(mode, temps, bounds, rois, lo = null, hi = null) {
  if (mode === 'frame') return [bounds[0], bounds[1]];
  if (mode === 'percentile') return percentileRange(temps, 2.0, 98.0);
  if (mode === 'rois') {
    const lows = rois.map(s => s.t_min_c).filter(v => v !== null && v !== undefined);
    const highs = rois.map(s => s.t_max_c).filter(v => v !== null && v !== undefined);
    if (!lows.length) throw new Error('an ROI range needs at least one ROI with valid pixels');
    return [Math.min(...lows), Math.max(...highs)];
  }
  if (mode === 'manual') {
    const a = number(lo, 'range lo');
    const b = number(hi, 'range hi');
    if (!(a < b)) throw new Error("a manual range's lo is below its hi");
    return [a, b];
  }
  throw new Error(`range mode is one of ${RANGE_MODES.join(', ')}`);
}

/**
 * Each ROI's T_avg against the reference ROI's, or null (the server's
 * delta_avg_c); `key` 't_min_c' or 't_max_c' gives delta_min_c / delta_max_c.
 */
export function deltasAgainst(values, referenceIndex, key = 't_avg_c') {
  const ref = referenceIndex === null || referenceIndex === undefined ? null : values[referenceIndex];
  const base = ref && ref[key] !== null && ref[key] !== undefined ? ref[key] : null;
  return values.map(v => (base !== null && v && v[key] !== null && v[key] !== undefined
    ? v[key] - base : null));
}

/** For the selected ROIs, a matrix of pairwise delta T_avg: cell [i][j] is row i minus column j. */
export function pairwiseDeltas(values) {
  return values.map(a => values.map(b => (a && b && a.t_avg_c !== null && a.t_avg_c !== undefined
    && b.t_avg_c !== null && b.t_avg_c !== undefined ? a.t_avg_c - b.t_avg_c : null)));
}

/** The colour the next ROI takes: the first of the set no ROI uses yet, else the one at its position. */
export function nextColour(rois) {
  const used = new Set(rois.map(r => String(r.colour || '').toUpperCase()));
  return COLOURS.find(c => !used.has(c)) || COLOURS[rois.length % COLOURS.length];
}

/** A label no other ROI has (labels are unique ignoring case, as the server requires). */
export function nextLabel(rois, base) {
  const taken = new Set(rois.map(r => String(r.label || '').toLowerCase()));
  for (let n = rois.length + 1; ; n += 1) {
    const label = `${base} ${n}`.slice(0, MAX_LABEL);
    if (!taken.has(label.toLowerCase())) return label;
  }
}

/** Why a set of ROIs cannot be saved as it is, or null: the server's own checks, said first here. */
export function roisProblem(rois) {
  if (rois.length > MAX_ROIS) return { code: 'too_many', count: MAX_ROIS };
  const seen = new Set();
  for (let i = 0; i < rois.length; i += 1) {
    const label = String(rois[i].label || '').split(/\s+/).filter(Boolean).join(' ');
    if (label.length < 1 || label.length > MAX_LABEL) return { code: 'label_length', position: i };
    if (seen.has(label.toLowerCase())) return { code: 'duplicate_label', position: i, label };
    seen.add(label.toLowerCase());
    try {
      normalise(rois[i].shape, rois[i].geometry);
    } catch (e) {
      return { code: 'geometry', position: i, message: e.message };
    }
  }
  return null;
}
